import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Bounce, toast } from "react-toastify"
import dayjs from "dayjs"
import { useNavigate, useSearchParams } from "react-router-dom"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import { Avatar } from "../../../components/DashboardComponents/AvatarGroup"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown, {
  toSingleFilterValue,
} from "../../../components/Listing/FilterDropdown"
import ListingTabs from "../../../components/Listing/ListingTabs"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import { ICONS } from "../../../constants/icons"
import { get, patch } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminPermission from "../../../hooks/useAdminPermission"
import useAdminCityFilterOptions from "../../../hooks/useAdminCityFilterOptions"
import {
  CALL_LIST_WITH,
  CALL_STATUS_OPTIONS,
  CALL_TYPE_OPTIONS,
  DEFAULT_CALL_TYPE,
  FORMAL_CALL_MODERATION_STATUS,
  FORMAL_CALL_STATUS_TABS,
  FORMAL_CALL_TAB_TO_API_STATUS,
  STATUS_LABEL,
  isFormalCallAwaitingApproval,
  mapCallRow,
  mapCallsSummaryKpi,
  mapFormalCallStatusTabs,
} from "./adminInboxData"

const PAGE_SIZE = 10

const TOAST_OPTIONS = { transition: Bounce }

/** Single-city filter — calls API uses city_ids[]=UUID */
const appendCityFilterParams = (
  params,
  { cityFilter = "", fixedCityId = "" } = {},
) => {
  const selected = fixedCityId
    ? String(fixedCityId)
    : Array.isArray(cityFilter)
      ? String(cityFilter[0] || "")
      : String(cityFilter || "")

  if (selected) params["city_ids[]"] = selected
}

/**
 * Admin Inbox & Calls — list, summary, and export via /api/v1/admin/calls.
 * Optional cityId (prop or ?city_id=) scopes to a Manage City listing.
 */
const AdminInbox = ({
  documentTitle = "Inbox & Calls | AMOK . EARTH Admin",
  cityId: cityIdProp,
  showKpi = true,
  detailBasePath = "/admin/inbox",
}) => {
  const navigate = useNavigate()
  const { can } = useAdminPermission()
  const canUpdate = can("calls", "edit")
  const [searchParams] = useSearchParams()
  const isCityScoped = Boolean(cityIdProp)
  const fixedCityId = cityIdProp || searchParams.get("city_id") || ""
  const { cityOptions } = useAdminCityFilterOptions({
    enabled: !isCityScoped,
  })
  const [callType, setCallType] = useState(DEFAULT_CALL_TYPE)
  const [typeOpen, setTypeOpen] = useState(false)
  const typeRef = useRef(null)
  const isFormal = callType === "formal"

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [cityFilter, setCityFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [activeTab, setActiveTab] = useState("all")
  const [tabItems, setTabItems] = useState(FORMAL_CALL_STATUS_TABS)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const [kpiCards, setKpiCards] = useState(() =>
    mapCallsSummaryKpi({}, DEFAULT_CALL_TYPE),
  )
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [isKpiLoading, setIsKpiLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [updatingCallId, setUpdatingCallId] = useState(null)
  const [error, setError] = useState(null)

  const typeLabel = useMemo(
    () =>
      CALL_TYPE_OPTIONS.find(option => option.value === callType)?.label ||
      callType,
    [callType],
  )

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [
    debouncedSearch,
    statusFilter,
    cityFilter,
    callType,
    fixedCityId,
    activeTab,
  ])

  useEffect(() => {
    setActiveTab("all")
    setStatusFilter("")
  }, [callType])

  useEffect(() => {
    if (!cityOptions.length || !cityFilter) return
    const allowed = new Set(cityOptions.map(option => String(option.value)))
    if (!allowed.has(String(cityFilter))) setCityFilter("")
  }, [cityFilter, cityOptions])

  useEffect(() => {
    if (!typeOpen) return undefined
    const handlePointer = event => {
      if (typeRef.current && !typeRef.current.contains(event.target)) {
        setTypeOpen(false)
      }
    }
    document.addEventListener("mousedown", handlePointer)
    return () => document.removeEventListener("mousedown", handlePointer)
  }, [typeOpen])

  const fetchSummary = useCallback(async () => {
    if (!showKpi && !isFormal) {
      setIsKpiLoading(false)
      return
    }
    if (showKpi) setIsKpiLoading(true)
    try {
      const params = { type: callType }
      appendCityFilterParams(params, { cityFilter, fixedCityId })
      const response = await get(endpoints.getAdminCallsSummary, { params })
      if (response?.data?.success) {
        const result = response.data.result || {}
        if (showKpi) {
          setKpiCards(mapCallsSummaryKpi(result, callType))
        }
        if (isFormal) {
          setTabItems(mapFormalCallStatusTabs(result))
        }
      } else {
        toastApiError(response, "Failed to load calls summary")
      }
    } catch (err) {
      toastApiError(err, "Failed to load calls summary")
    } finally {
      if (showKpi) setIsKpiLoading(false)
    }
  }, [callType, cityFilter, fixedCityId, isFormal, showKpi])

  const buildListParams = useCallback(
    ({ includePage = true } = {}) => {
      const params = {
        "with[]": CALL_LIST_WITH,
        type: callType,
        sort_by: "created_at",
        sort_direction: "desc",
      }
      if (includePage) {
        params.page = currentPage
        params.page_size = pageSize
      }
      if (debouncedSearch) params.search = debouncedSearch

      if (isFormal) {
        const apiStatus = FORMAL_CALL_TAB_TO_API_STATUS[activeTab]
        if (apiStatus) params.status = apiStatus
      } else if (statusFilter) {
        params.status = statusFilter
      }

      appendCityFilterParams(params, { cityFilter, fixedCityId })
      return params
    },
    [
      activeTab,
      callType,
      cityFilter,
      currentPage,
      debouncedSearch,
      fixedCityId,
      isFormal,
      pageSize,
      statusFilter,
    ],
  )

  const fetchCalls = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminCalls, {
        params: buildListParams(),
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapCallRow)

        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total ?? mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load calls")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load calls")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, currentPage])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    fetchCalls()
  }, [fetchCalls])

  const handleExport = async () => {
    if (isExporting) return

    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminCalls, {
        params: buildListParams({ includePage: false }),
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export calls")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export calls"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, TOAST_OPTIONS)
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `calls-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Calls exported.", TOAST_OPTIONS)
    } catch (err) {
      toastApiError(err, "Failed to export calls")
    } finally {
      setIsExporting(false)
    }
  }

  const handleViewDetail = row => {
    if (!row?.id) return
    navigate(`${detailBasePath}/${row.id}`)
  }

  const updateCallStatus = async (row, nextAction) => {
    if (!row?.id || updatingCallId) return

    const apiStatus = FORMAL_CALL_MODERATION_STATUS[nextAction]
    if (!apiStatus) return

    setUpdatingCallId(row.id)
    try {
      const response = await patch(endpoints.updateAdminCall(row.id), {
        status: apiStatus,
      })
      if (response?.data?.success) {
        toast.success(
          nextAction === "approved" ? "Call approved." : "Call rejected.",
          TOAST_OPTIONS,
        )
        fetchSummary()
        fetchCalls()
      } else {
        toastApiError(response, "Failed to update call")
      }
    } catch (err) {
      toastApiError(err, "Failed to update call")
    } finally {
      setUpdatingCallId(null)
    }
  }

  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / pageSize) || 1),
  )

  const filterConfig = useMemo(() => {
    const filters = []

    if (!isCityScoped) {
      filters.push({
        key: "city",
        type: "select",
        label: "City",
        multiple: false,
        searchable: true,
        options: cityOptions,
        value: cityFilter,
        defaultValue: "",
        onChange: next => {
          const value = Array.isArray(next)
            ? String(next[0] || "")
            : String(next || "")
          setCityFilter(value)
        },
      })
    }

    // Formal uses status tabs (like City Calls); informal keeps status filter.
    if (!isFormal) {
      filters.push({
        key: "status",
        type: "select",
        label: "Status",
        options: CALL_STATUS_OPTIONS,
        value: statusFilter,
        defaultValue: "",
        onChange: next => {
          setStatusFilter(toSingleFilterValue(next))
        },
      })
    }

    return filters
  }, [cityFilter, cityOptions, isCityScoped, isFormal, statusFilter])

  const repliesHeader = isFormal ? "Responses" : "Replies"
  const showAwaitingActions = (row, type) =>
    canUpdate && isFormalCallAwaitingApproval(row, type)

  return (
    <div className="admin-page admin-inbox">
      <div className="admin-inbox__kpi-header">
        <div className="admin-inbox__type-filter" ref={typeRef}>
          <CustomButton
            variant="transparent"
            size="md"
            type="button"
            trailingIconSrc={ICONS.chevronDown}
            trailingIconAlt=""
            aria-haspopup="listbox"
            aria-expanded={typeOpen}
            onClick={() => setTypeOpen(open => !open)}
          >
            {typeLabel}
          </CustomButton>
          {typeOpen ? (
            <ul className="admin-inbox__type-menu" role="listbox">
              {CALL_TYPE_OPTIONS.map(option => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === callType}
                    className={
                      option.value === callType
                        ? "admin-inbox__type-option admin-inbox__type-option--active"
                        : "admin-inbox__type-option"
                    }
                    onClick={() => {
                      setCallType(option.value)
                      setTypeOpen(false)
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {showKpi ? (
          <div
            className={`admin-inbox__kpi-grid${
              kpiCards.length >= 4
                ? " admin-inbox__kpi-grid--four"
                : " admin-inbox__kpi-grid--three"
            }`}
          >
            {kpiCards.map(card => (
              <DashboardCard
                key={card.key}
                title={card.title}
                tone={card.tone}
                className="admin-inbox__kpi-card"
                fullHeight
              >
                {isKpiLoading ? (
                  <span className="admin-inbox__skeleton admin-inbox__skeleton--kpi" />
                ) : (
                  <p className="admin-inbox__kpi-value">{card.value}</p>
                )}
              </DashboardCard>
            ))}
          </div>
        ) : null}
      </div>

      <div className="admin-inbox__toolbar">
        <h1 className="admin-inbox__title">Inbox & Calls</h1>
        <div className="admin-inbox__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search topics..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-inbox__search"
          />
          {filterConfig.length > 0 ? (
            <FilterDropdown label="Filter" filters={filterConfig} />
          ) : null}
          <ExportLogButton
            entityLabel="calls"
            onClick={handleExport}
            loading={isExporting}
          />
        </div>
      </div>

      {isFormal ? (
        <div className="admin-inbox__tabs-row">
          <ListingTabs
            items={tabItems}
            activeKey={activeTab}
            onChange={setActiveTab}
          />
        </div>
      ) : null}

      <div className="admin-inbox__table-wrapper" aria-busy={isLoading}>
        <div className="admin-inbox__table" role="table">
          <div
            className="admin-inbox__table-row admin-inbox__table-row--head"
            role="row"
          >
            <div role="columnheader">Posted By</div>
            <div role="columnheader">Title</div>
            <div role="columnheader">Category</div>
            <div role="columnheader">City</div>
            <div role="columnheader">{repliesHeader}</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Posted Date</div>
            <div role="columnheader" className="admin-inbox__actions-head">
              Actions
            </div>
          </div>

          {isLoading
            ? skeletonRows.map(key => (
                <div
                  key={key}
                  className="admin-inbox__table-row admin-inbox__table-row--skeleton"
                  role="row"
                >
                  <div className="admin-inbox__skeleton" />
                  <div className="admin-inbox__skeleton" />
                  <div className="admin-inbox__skeleton" />
                  <div className="admin-inbox__skeleton" />
                  <div className="admin-inbox__skeleton" />
                  <div className="admin-inbox__skeleton" />
                  <div className="admin-inbox__skeleton" />
                  <div className="admin-inbox__skeleton" />
                </div>
              ))
            : rows.map(row => (
                <div key={row.id} className="admin-inbox__table-row" role="row">
                  <div className="admin-inbox__poster" role="cell">
                    <Avatar
                      src={row.poster?.avatar}
                      name={row.poster.name}
                      size="sm"
                      className="admin-inbox__avatar"
                    />
                    <TruncatedText className="admin-inbox__poster-name">
                      {row.poster.name}
                    </TruncatedText>
                  </div>
                  <TruncatedText
                    as="div"
                    className="admin-inbox__cell admin-inbox__cell--title"
                    role="cell"
                  >
                    {row.title}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-inbox__cell"
                    role="cell"
                  >
                    {row.category}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-inbox__cell"
                    role="cell"
                  >
                    {row.city}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-inbox__cell"
                    role="cell"
                  >
                    {row.replies}
                  </TruncatedText>
                  <div className="admin-inbox__status-cell" role="cell">
                    <span
                      className={`admin-inbox__status admin-inbox__status--${row.status}`}
                    >
                      <span
                        className="admin-inbox__status-dot"
                        aria-hidden="true"
                      />
                      {STATUS_LABEL[row.status] || row.status}
                    </span>
                  </div>
                  <TruncatedText
                    as="div"
                    className="admin-inbox__cell"
                    role="cell"
                  >
                    {row.postedDate}
                  </TruncatedText>
                  <div className="admin-inbox__actions" role="cell">
                    {showAwaitingActions(row, callType) ? (
                      <>
                        <CustomButton
                          variant="transparent"
                          size="sm"
                          type="button"
                          className="admin-inbox__approve-btn"
                          disabled={Boolean(updatingCallId)}
                          onClick={() => updateCallStatus(row, "approved")}
                        >
                          Approve
                        </CustomButton>
                        <CustomButton
                          variant="transparent"
                          size="sm"
                          type="button"
                          className="admin-inbox__reject-btn"
                          disabled={Boolean(updatingCallId)}
                          onClick={() => updateCallStatus(row, "rejected")}
                        >
                          Reject
                        </CustomButton>
                        <CustomButton
                          variant="transparent"
                          size="sm"
                          type="button"
                          trailingIconSrc={ICONS.arrowRight12}
                          trailingIconAlt=""
                          onClick={() => handleViewDetail(row)}
                        >
                          View Detail
                        </CustomButton>
                      </>
                    ) : (
                      <CustomButton
                        variant="transparent"
                        size="sm"
                        type="button"
                        trailingIconSrc={ICONS.arrowRight12}
                        trailingIconAlt=""
                        onClick={() => handleViewDetail(row)}
                      >
                        View Detail
                      </CustomButton>
                    )}
                  </div>
                </div>
              ))}

          {!isLoading && rows.length === 0 && (
            <div className="admin-inbox__empty">
              <EmptyState
                title={error ? "Couldn’t load calls" : "No calls found"}
                description={error || "Try adjusting your search or filters."}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel={isFormal ? "Requests" : "Topics"}
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-inbox__pagination"
      />
    </div>
  )
}

export default AdminInbox

AdminInbox.propTypes = {
  documentTitle: PropTypes.string,
  cityId: PropTypes.string,
  showKpi: PropTypes.bool,
  detailBasePath: PropTypes.string,
}

AdminInbox.defaultProps = {
  documentTitle: "Inbox & Calls | AMOK . EARTH Admin",
  cityId: undefined,
  showKpi: true,
  detailBasePath: "/admin/inbox",
}
